import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
    Card,
    Select,
    Button,
    Table,
    Tag,
    Space,
    Typography,
    Tooltip,
    Dropdown,
} from 'antd';
import {
    CalendarOutlined,
    SaveOutlined,
    ReloadOutlined,
    CalculatorOutlined,
    CheckOutlined,
    CloseOutlined,
    SettingOutlined,
    DownOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getRequest, postRequest } from 'api';
import { getStoredWorkMonth } from 'utils/workMonthStorage';
import { ClassLevelSelect } from 'Components';
import './ClassAttendanceBoard.css';

const { Text } = Typography;
const { Option } = Select;

interface DayInfo {
    day: number;
    dayOfWeek: number;
    dayOfWeekName: string;
    isWeekend?: boolean;
    isSaturday?: boolean;
    isSunday?: boolean;
}

interface StudentAttendanceRow {
    studentClassYearId: number;
    studentId: number;
    studentCode: string;
    studentName: string;
    parentName: string;
    phone: string;
    days: Record<string, number>;
    totalUnits: number;
}

interface MonthAttendanceBoardData {
    classYearId: number;
    className: string;
    month: number;
    year: number;
    totalDays?: number;
    daysInMonth?: number;
    dayOfWeekMap?: Record<string, string>;
    days?: DayInfo[];
    scheduleType?: number;
    isSettled?: boolean;
    students: StudentAttendanceRow[];
}

interface ClassYearOption {
    classYearId: number;
    className: string;
    schoolYear: string;
    classLevel: string;
}

const VI_DAY_OF_WEEK: Record<string, { label: string; isSat: boolean; isSun: boolean }> = {
    'THU_HAI': { label: 'T2', isSat: false, isSun: false },
    'THU_BA': { label: 'T3', isSat: false, isSun: false },
    'THU_TU': { label: 'T4', isSat: false, isSun: false },
    'THU_NAM': { label: 'T5', isSat: false, isSun: false },
    'THU_SAU': { label: 'T6', isSat: false, isSun: false },
    'THU_BAY': { label: 'T7', isSat: true, isSun: false },
    'CHU_NHAT': { label: 'CN', isSat: false, isSun: true },
};

export const ClassAttendanceBoard: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const schoolIdStr = localStorage.getItem('schoolId');
    const schoolId = schoolIdStr ? parseInt(schoolIdStr, 10) : null;

    const [selectedYear, setSelectedYear] = useState<dayjs.Dayjs | null>(() => dayjs(`${getStoredWorkMonth().year}`, 'YYYY'));
    const [selectedMonth, setSelectedMonth] = useState<number>(() => getStoredWorkMonth().month);
    const [selectedClassYearId, setSelectedClassYearId] = useState<number | null>(null);
    const [selectedClassLevel, setSelectedClassLevel] = useState<string | undefined>(undefined);

    const [classList, setClassList] = useState<ClassYearOption[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [boardData, setBoardData] = useState<MonthAttendanceBoardData | null>(null);

    const [localAttendance, setLocalAttendance] = useState<Record<number, Record<string, number>>>({});
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);

    // Lắng nghe thay đổi kỳ làm việc từ cấu hình
    useEffect(() => {
        const handleWorkMonthChange = (e: any) => {
            const { month, year } = e.detail || getStoredWorkMonth();
            setSelectedMonth(month);
            setSelectedYear(dayjs(`${year}`, 'YYYY'));
        };
        window.addEventListener('workMonthChanged', handleWorkMonthChange);
        return () => window.removeEventListener('workMonthChanged', handleWorkMonthChange);
    }, []);

    // Fetch classes
    const fetchClasses = useCallback(async (year: number) => {
        if (!schoolId) return;
        try {
            const res: any = await getRequest(
                `/cms/class/get-class-years-by-start-end-schoolId?start=${year}&schoolId=${schoolId}&page=1&perPage=200&sort=asc&sortBy=className`
            );
            if (res?.code === 200 && res.info?.list) {
                setClassList(res.info.list);
                const paramClassId = searchParams.get('classYearId');
                if (paramClassId) {
                    const id = parseInt(paramClassId, 10);
                    if (res.info.list.some((c: any) => c.classYearId === id)) {
                        setSelectedClassYearId(id);
                        return;
                    }
                }
                if (!selectedClassYearId && res.info.list.length > 0) {
                    setSelectedClassYearId(res.info.list[0].classYearId);
                }
            }
        } catch {
            toast.error('Lỗi khi tải danh sách lớp');
        }
    }, [schoolId, searchParams, selectedClassYearId]);

    useEffect(() => {
        if (selectedYear) {
            fetchClasses(selectedYear.year());
        }
    }, [selectedYear, fetchClasses]);

    const filteredClassList = useMemo(() => {
        if (!selectedClassLevel) return classList;
        return classList.filter((c) => String(c.classLevel).trim() === selectedClassLevel.trim());
    }, [classList, selectedClassLevel]);

    const handleClassLevelChange = (level?: string) => {
        setSelectedClassLevel(level);
        const nextList = level
            ? classList.filter((c) => String(c.classLevel).trim() === level.trim())
            : classList;
        if (nextList.length > 0) {
            if (!nextList.some((c) => c.classYearId === selectedClassYearId)) {
                setSelectedClassYearId(nextList[0].classYearId);
                setSearchParams({ classYearId: String(nextList[0].classYearId) });
            }
        } else {
            setSelectedClassYearId(null);
            setBoardData(null);
        }
    };

    // Fetch attendance board data
    const fetchAttendanceData = useCallback(async () => {
        if (!selectedClassYearId || !selectedYear) return;
        setLoading(true);
        try {
            const yearNum = selectedYear.year();
            const res: any = await getRequest(
                `/cms/attendance/month-board?classYearId=${selectedClassYearId}&month=${selectedMonth}&year=${yearNum}`
            );
            if (res?.code === 200 && res.info) {
                const data: MonthAttendanceBoardData = res.info;
                setBoardData(data);

                // Populate local state
                const matrix: Record<number, Record<string, number>> = {};
                (data.students || []).forEach((st) => {
                    matrix[st.studentClassYearId] = { ...(st.days || {}) };
                });
                setLocalAttendance(matrix);
                setHasUnsavedChanges(false);
            } else {
                toast.error(res?.msg || 'Không thể tải dữ liệu chấm ngày');
            }
        } catch {
            toast.error('Lỗi khi tải bảng chấm ngày');
        } finally {
            setLoading(false);
        }
    }, [selectedClassYearId, selectedMonth, selectedYear]);

    useEffect(() => {
        fetchAttendanceData();
    }, [fetchAttendanceData]);

    // Toggle cell attendance (0 <-> 1)
    const handleToggleCell = useCallback((studentClassYearId: number, dayStr: string) => {
        setLocalAttendance((prev) => {
            const currentStudentDays = { ...(prev[studentClassYearId] || {}) };
            const currentVal = currentStudentDays[dayStr] ?? 0;
            currentStudentDays[dayStr] = currentVal === 1 ? 0 : 1;
            return {
                ...prev,
                [studentClassYearId]: currentStudentDays,
            };
        });
        setHasUnsavedChanges(true);
    }, []);

    // Toggle whole column (mark all students present or absent on a specific day)
    const handleToggleDayForAll = useCallback((dayStr: string, value: number) => {
        if (!boardData || boardData.isSettled) return;
        setLocalAttendance((prev) => {
            const next = { ...prev };
            boardData.students.forEach((st) => {
                const studentDays = { ...(next[st.studentClassYearId] || {}) };
                studentDays[dayStr] = value;
                next[st.studentClassYearId] = studentDays;
            });
            return next;
        });
        setHasUnsavedChanges(true);
        toast.info(`Đã đặt ngày ${dayStr} cho toàn bộ lớp thành: ${value === 1 ? 'Đi học' : 'Nghỉ'}`);
    }, [boardData]);

    // Preset Init
    const handleInitPreset = async (presetType: 'T2_T6' | 'T2_T7' | 'ALL') => {
        if (!selectedClassYearId) {
            toast.warning('Vui lòng chọn lớp học trước khi khởi tạo lịch!');
            return;
        }
        if (!selectedYear) {
            toast.warning('Vui lòng chọn năm học!');
            return;
        }
        setLoading(true);
        try {
            const schedType = presetType === 'T2_T6' ? 1 : presetType === 'T2_T7' ? 2 : 3;
            const res: any = await postRequest('/cms/attendance/month-board/init', {
                classYearId: selectedClassYearId,
                month: selectedMonth,
                year: selectedYear.year(),
                scheduleType: schedType,
                presetType,
            });
            if (res?.code === 200) {
                toast.success('Khởi tạo lịch chuẩn thành công!');
                setHasUnsavedChanges(false);
                if (res.info && typeof res.info === 'object' && res.info.students) {
                    const data: MonthAttendanceBoardData = res.info;
                    setBoardData(data);
                    const matrix: Record<number, Record<string, number>> = {};
                    (data.students || []).forEach((st) => {
                        matrix[st.studentClassYearId] = { ...(st.days || {}) };
                    });
                    setLocalAttendance(matrix);
                } else {
                    await fetchAttendanceData();
                }
            } else {
                toast.error(res?.msg || 'Lỗi khi khởi tạo lịch');
            }
        } catch {
            toast.error('Lỗi kết nối khi khởi tạo lịch');
        } finally {
            setLoading(false);
        }
    };

    // Save Attendance
    const handleSaveAttendance = async () => {
        if (!selectedClassYearId || !selectedYear || !boardData) return;
        setSaving(true);
        try {
            const studentAttendanceList = boardData.students.map((st) => ({
                studentClassYearId: st.studentClassYearId,
                days: localAttendance[st.studentClassYearId] || {},
            }));

            const res: any = await postRequest('/cms/attendance/month-board/save', {
                classYearId: selectedClassYearId,
                month: selectedMonth,
                year: selectedYear.year(),
                students: studentAttendanceList,
                studentAttendanceList,
            });

            if (res?.code === 200) {
                toast.success('Lưu dữ liệu chấm ngày thành công!');
                setHasUnsavedChanges(false);
                fetchAttendanceData();
            } else {
                toast.error(res?.msg || 'Lỗi khi lưu chấm ngày');
            }
        } catch {
            toast.error('Lỗi khi gửi dữ liệu lưu chấm ngày');
        } finally {
            setSaving(false);
        }
    };

    // Calculate total days in month from boardData
    const numDays = useMemo(() => {
        if (!boardData) return 31;
        return boardData.totalDays || boardData.daysInMonth || (boardData.days ? boardData.days.length : 31);
    }, [boardData]);

    // Table rows with real-time recalculation of total units
    const tableData = useMemo(() => {
        if (!boardData || !boardData.students) return [];
        return boardData.students.map((st, idx) => {
            const daysMap = localAttendance[st.studentClassYearId] || {};
            let computedUnits = 0;
            for (let d = 1; d <= numDays; d++) {
                if (daysMap[String(d)] === 1) {
                    computedUnits++;
                }
            }
            return {
                ...st,
                stt: idx + 1,
                days: daysMap,
                totalUnits: computedUnits,
            };
        });
    }, [boardData, localAttendance, numDays]);

    // Summary calculations
    const totalClassDaysWorked = useMemo(() => {
        return tableData.reduce((acc, r) => acc + r.totalUnits, 0);
    }, [tableData]);

    // Table columns
    const columns = useMemo<ColumnsType<any>>(() => {
        if (!boardData) return [];

        const fixedCols: ColumnsType<any> = [
            {
                title: 'STT',
                dataIndex: 'stt',
                key: 'stt',
                width: 45,
                align: 'center',
                fixed: 'left',
            },
            {
                title: 'Mã HS',
                dataIndex: 'studentCode',
                key: 'studentCode',
                width: 100,
                align: 'center',
                fixed: 'left',
                render: (val) => <Text strong>{val || '-'}</Text>,
            },
            {
                title: 'Họ và tên',
                dataIndex: 'studentName',
                key: 'studentName',
                width: 170,
                fixed: 'left',
                render: (val, r) => <Text strong style={{ color: '#262626' }}>{val || r.fullName || ''}</Text>,
            },
        ];

        // Day columns 1..numDays
        const dayCols: ColumnsType<any> = [];
        for (let d = 1; d <= numDays; d++) {
            const dayStr = String(d);
            let dowLabel = '';
            let isSat = false;
            let isSun = false;

            if (boardData.days && boardData.days.length > 0) {
                const dayObj = boardData.days.find((item) => item.day === d) || boardData.days[d - 1];
                if (dayObj) {
                    dowLabel = dayObj.dayOfWeekName || '';
                    isSat = !!dayObj.isSaturday || dayObj.dayOfWeek === 6 || dowLabel === 'T7';
                    isSun = !!dayObj.isSunday || dayObj.dayOfWeek === 7 || dowLabel === 'CN';
                }
            } else if (boardData.dayOfWeekMap) {
                const dowKey = boardData.dayOfWeekMap[dayStr] || '';
                const dowInfo = VI_DAY_OF_WEEK[dowKey];
                if (dowInfo) {
                    dowLabel = dowInfo.label;
                    isSat = dowInfo.isSat;
                    isSun = dowInfo.isSun;
                } else {
                    dowLabel = dowKey;
                    isSat = dowKey === 'T7' || dowKey === 'THU_BAY';
                    isSun = dowKey === 'CN' || dowKey === 'CHU_NHAT';
                }
            }

            const dayMenu = {
                items: [
                    {
                        key: '1',
                        label: (
                            <Space><CheckOutlined style={{ color: '#52c41a' }} /> Đặt cả lớp đi học (1)</Space>
                        ),
                        onClick: () => handleToggleDayForAll(dayStr, 1),
                    },
                    {
                        key: '0',
                        label: (
                            <Space><CloseOutlined style={{ color: '#ff4d4f' }} /> Đặt cả lớp nghỉ (0)</Space>
                        ),
                        onClick: () => handleToggleDayForAll(dayStr, 0),
                    },
                ],
            };

            dayCols.push({
                title: (
                    <Dropdown menu={dayMenu} trigger={['click']} disabled={Boolean(boardData.isSettled)}>
                        <div style={{ cursor: 'pointer', userSelect: 'none' }} title="Nhấn để đổi nhanh cả lớp">
                            <div style={{ fontSize: 13, fontWeight: 700 }}>{d < 10 ? `0${d}` : d}</div>
                            <div className={isSat ? 'header-sat' : isSun ? 'header-sun' : ''} style={{ fontSize: 10 }}>
                                {dowLabel}
                            </div>
                        </div>
                    </Dropdown>
                ),
                key: `day_${d}`,
                width: 38,
                align: 'center',
                className: isSat ? 'col-saturday' : isSun ? 'col-sunday' : undefined,
                render: (_, record) => {
                    const val = record.days[dayStr] ?? 0;
                    const isPresent = val === 1;
                    return (
                        <div
                            className={`attendance-day-cell ${isPresent ? 'present' : 'absent'}`}
                            onClick={() => { if (!boardData.isSettled) handleToggleCell(record.studentClassYearId, dayStr); }}
                            title={`Ngày ${d} (${dowLabel}): ${isPresent ? 'Đi học (1)' : 'Nghỉ (0)'}. Nhấp để đổi.`}
                        >
                            {val}
                        </div>
                    );
                },
            });
        }

        // Summary column Cộng
        const sumCol: ColumnsType<any> = [
            {
                title: 'Cộng',
                dataIndex: 'totalUnits',
                key: 'totalUnits',
                width: 70,
                align: 'center',
                fixed: 'right',
                render: (val) => (
                    <Tag color="purple" style={{ fontSize: 13, fontWeight: 700, padding: '2px 8px', borderRadius: 12 }}>
                        {val}
                    </Tag>
                ),
            },
        ];

        return [...fixedCols, ...dayCols, ...sumCol];
    }, [boardData, numDays, handleToggleCell, handleToggleDayForAll]);

    const presetMenuItems = [
        {
            key: 'T2_T6',
            label: (
                <div style={{ padding: '4px 0' }}>
                    <div style={{ fontWeight: 600 }}>Lịch học Thứ 2 - Thứ 6</div>
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>Tự động đặt Nghỉ Thứ 7 &amp; Chủ nhật</div>
                </div>
            ),
            onClick: () => handleInitPreset('T2_T6'),
        },
        {
            key: 'T2_T7',
            label: (
                <div style={{ padding: '4px 0' }}>
                    <div style={{ fontWeight: 600 }}>Lịch học Thứ 2 - Thứ 7</div>
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>Tự động đặt Nghỉ Chủ nhật</div>
                </div>
            ),
            onClick: () => handleInitPreset('T2_T7'),
        },
        {
            key: 'ALL',
            label: (
                <div style={{ padding: '4px 0' }}>
                    <div style={{ fontWeight: 600 }}>Học cả tuần (T2 - CN)</div>
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>Tất cả các ngày trong tháng đều đi học</div>
                </div>
            ),
            onClick: () => handleInitPreset('ALL'),
        },
    ];

    return (
        <div className="class-attendance-page">
            {/* TOOLBAR */}
            <Card
                bordered={false}
                style={{
                    borderRadius: 12,
                    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                    marginBottom: 16,
                    background: '#ffffff',
                }}
                bodyStyle={{ padding: '16px 20px' }}
            >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
                    {/* Left: Filters */}
                    <Space wrap size={16} align="center">

                        <Tag
                            color="blue"
                            style={{
                                fontSize: 13,
                                padding: '4px 12px',
                                borderRadius: 4,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                fontWeight: 600,
                            }}
                        >
                            <CalendarOutlined /> Tháng {selectedMonth}/{selectedYear ? selectedYear.year() : ''}
                        </Tag>

                        <Space size={6} align="center">
                            <Text strong style={{ color: '#595959', fontSize: 13 }}>Khối:</Text>
                            <ClassLevelSelect
                                value={selectedClassLevel}
                                onChange={handleClassLevelChange}
                                placeholder="Tất cả khối"
                                style={{ width: 130 }}
                            />
                        </Space>

                        <Space size={6} align="center">
                            <Text strong style={{ color: '#595959', fontSize: 13 }}>Lớp:</Text>
                            <Select
                                value={selectedClassYearId || undefined}
                                onChange={(val) => {
                                    setSelectedClassYearId(val);
                                    setSearchParams({ classYearId: String(val) });
                                }}
                                placeholder="Chọn lớp"
                                style={{ width: 160 }}
                                showSearch
                                optionFilterProp="children"
                            >
                                {filteredClassList.map((c) => (
                                    <Option key={c.classYearId} value={c.classYearId}>
                                        {c.className}
                                    </Option>
                                ))}
                            </Select>
                        </Space>

                        <Tooltip title="Tải lại dữ liệu">
                            <Button icon={<ReloadOutlined />} onClick={fetchAttendanceData} loading={loading} />
                        </Tooltip>
                    </Space>

                    {/* Right: Actions */}
                    <Space wrap size={10} align="center">
                        <Dropdown menu={{ items: presetMenuItems }} trigger={['click']} placement="bottomLeft" disabled={Boolean(boardData?.isSettled)}>
                            <Button icon={<SettingOutlined />} loading={loading} disabled={Boolean(boardData?.isSettled)} style={{ fontWeight: 500 }}>
                                Khởi tạo lịch chuẩn <DownOutlined />
                            </Button>
                        </Dropdown>

                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            loading={saving}
                            onClick={handleSaveAttendance}
                            disabled={Boolean(boardData?.isSettled)}
                            style={{
                                backgroundColor: hasUnsavedChanges ? '#fa8c16' : '#1890ff',
                                borderColor: hasUnsavedChanges ? '#fa8c16' : '#1890ff',
                                fontWeight: 600,
                            }}
                        >
                            {hasUnsavedChanges ? 'Lưu chấm ngày (Có thay đổi)' : 'Lưu chấm ngày'}
                        </Button>

                        <Button
                            type="default"
                            icon={<CalculatorOutlined />}
                            onClick={() => navigate(`/class-actual-settle?classYearId=${selectedClassYearId || ''}`)}
                            style={{
                                borderColor: '#52c41a',
                                color: '#52c41a',
                                fontWeight: 600,
                            }}
                        >
                            Chốt thực tế &gt;
                        </Button>
                    </Space>
                </div>

                {/* Sub-bar Statistics */}
                <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                        Tổng số học sinh: <Text strong style={{ color: '#1890ff' }}>{tableData.length}</Text>
                    </Text>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                        Tổng ngày đi học lớp: <Text strong style={{ color: '#722ed1' }}>{totalClassDaysWorked}</Text> suất
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>
                        * Ghi chú: Nhấp vào từng ô ngày để chuyển đổi 1 (Đi học) và 0 (Nghỉ). Nhấp vào tiêu đề ngày để đổi nhanh cả lớp.
                    </Text>
                </div>
            </Card>

            {/* ATTENDANCE MATRIX TABLE */}
            <Card
                bordered={false}
                style={{
                    borderRadius: 12,
                    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                    background: '#ffffff',
                }}
                bodyStyle={{ padding: 12 }}
            >
                <Table
                    columns={columns}
                    dataSource={tableData}
                    rowKey="studentClassYearId"
                    loading={loading}
                    pagination={false}
                    bordered
                    size="small"
                    scroll={{ x: 'max-content', y: 'calc(100vh - 310px)' }}
                />
            </Card>
        </div>
    );
};

export default ClassAttendanceBoard;
